import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { AiLessonChatRole, useAiLessonChatMutation } from '@api/graphql-codegen';
import Header from '@src/components/header/Header';
import FooterPage from '@src/components/footer-page/FooterPage';
import Button from '@src/components/button/Button';
import Spinner from '@src/components/spinner/Spinner';
import { useUser } from '@hooks';
import AiAnswer from './AiAnswer';
import AiLessonGrid from './AiLessonGrid';
import { AiLessonCard, isAiSearchUnavailable, presentCards, useAiGradeOptions } from './aiSearch';
import SimulateSchool from './SimulateSchool';
import { useSimulateSchool } from './useSimulateSchool';
import styles from './AiDashboard.module.scss';

const STARTER_PROMPTS = [
  'A student has stopped joining in at recess',
  'My class is anxious about upcoming tests',
  "A student's grandparent has recently died",
];

const MAX_MESSAGE_LENGTH = 2000;

// The server keeps only the last six turns, so there is no point sending more.
const HISTORY_TURNS = 6;

type UserTurn = { id: number; role: 'user'; content: string };
type AssistantTurn = {
  id: number;
  role: 'assistant';
  content: string;
  sources: { namespace: string; name: string }[];
  lessons: AiLessonCard[];
};
type FailedTurn = { id: number; role: 'failed'; question: string; unavailable: boolean };
type Turn = UserTurn | AssistantTurn | FailedTurn;

/**
 * AI dashboard (OP-3164): a chat grounded in Open Parachute guidance, with the lessons the latest
 * answer recommends alongside it. Every recommended lesson has passed the school's entitlement and
 * exclusion gates on the server before it arrives here.
 */
function AiDashboard() {
  const [user] = useUser();
  const simulate = useSimulateSchool();
  const { options: gradeOptions, isAUuser } = useAiGradeOptions(simulate.target);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [grade, setGrade] = useState('');
  const nextId = useRef(1);
  const endRef = useRef<HTMLDivElement>(null);

  const [sendChat, { loading }] = useAiLessonChatMutation();

  // A conversation belongs to the school and ranking it was simulated with. Switching either starts
  // afresh, so earlier answers and recommendations are never read as the new choice's.
  const simulationKey = `${simulate.asSchoolId ?? ''}|${simulate.approach}`;
  const firstSimulationRef = useRef(true);
  useEffect(() => {
    if (firstSimulationRef.current) {
      firstSimulationRef.current = false;
      return;
    }
    setTurns([]);
    setGrade('');
  }, [simulationKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, loading]);

  const recommendations = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      const turn = turns[i];
      if (turn.role === 'assistant') return turn;
    }
    return null;
  }, [turns]);

  const ask = async (question: string, prior: Turn[]) => {
    const history = prior
      .filter((t): t is UserTurn | AssistantTurn => t.role === 'user' || t.role === 'assistant')
      .slice(-HISTORY_TURNS)
      .map((t) => ({
        role: t.role === 'user' ? AiLessonChatRole.USER : AiLessonChatRole.ASSISTANT,
        content: t.content,
      }));

    setTurns([...prior, { id: nextId.current++, role: 'user', content: question }]);

    try {
      const { data } = await sendChat({
        variables: { input: { query: question, grade: grade || undefined, history, asSchoolId: simulate.asSchoolId, sendAccess: simulate.sendAccess } },
      });
      const reply = data?.aiLessonChat;
      if (!reply) throw new Error('empty reply');
      setTurns((current) => [...current, {
        id: nextId.current++,
        role: 'assistant',
        content: reply.answer,
        sources: reply.sources,
        lessons: presentCards(reply.lessons),
      }]);
    } catch (error: any) {
      setTurns((current) => [...current, {
        id: nextId.current++,
        role: 'failed',
        question,
        unavailable: isAiSearchUnavailable(error),
      }]);
    }
  };

  const submit = (text: string) => {
    const question = text.trim();
    if (!question || loading) return;
    setDraft('');
    ask(question, turns);
  };

  const retry = (failed: FailedTurn) => {
    // Resend from just before the question that failed, so it is not sent twice as history.
    const index = turns.findIndex((t) => t.id === failed.id);
    const prior = turns.slice(0, Math.max(0, index - 1));
    ask(failed.question, prior);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(draft);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(draft);
    }
  };

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <SimulateSchool simulate={simulate} />
          <header className={styles.intro}>
            <h1>{user?.firstName ? `Hi ${user.firstName}, what's happening in your classroom?` : "What's happening in your classroom?"}</h1>
            <p>Describe a situation and get practical guidance from Open Parachute, with lessons to match.</p>
          </header>

          <div className={styles.layout}>
            <section className={styles.chat} aria-label="Chat">
              <div className={styles.messages} aria-live="polite">
                {turns.length === 0 && (
                  <div className={styles.starters}>
                    <p>Try one of these, or describe your own:</p>
                    {STARTER_PROMPTS.map((prompt) => (
                      <button key={prompt} type="button" className={styles.starter} onClick={() => submit(prompt)}>
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}

                {turns.map((turn) => {
                  if (turn.role === 'user') {
                    return <div key={turn.id} className={styles.userMessage}>{turn.content}</div>;
                  }
                  if (turn.role === 'failed') {
                    return (
                      <div key={turn.id} className={styles.failedMessage}>
                        <p>
                          {turn.unavailable
                            ? 'Guidance is unavailable right now.'
                            : "That didn't work this time."}
                        </p>
                        <button type="button" onClick={() => retry(turn)} disabled={loading}>Try again</button>
                      </div>
                    );
                  }
                  return (
                    <div key={turn.id} className={styles.assistantMessage}>
                      <AiAnswer text={turn.content} className={styles.answer} />
                      {turn.sources.length > 0 && (
                        <p className={styles.sources}>
                          Drawn from: {turn.sources.slice(0, 4).map((s) => s.name).join(' · ')}
                        </p>
                      )}
                    </div>
                  );
                })}

                {loading && (
                  <div className={styles.thinking}>
                    <Spinner />
                    <span>Thinking it through with Open Parachute guidance…</span>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              <form className={styles.composer} onSubmit={handleSubmit}>
                <textarea
                  value={draft}
                  maxLength={MAX_MESSAGE_LENGTH}
                  rows={2}
                  placeholder={turns.length ? 'Add more detail or ask a follow-up…' : 'Describe the situation…'}
                  aria-label="Message"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <div className={styles.composerActions}>
                  {gradeOptions.length > 0 && (
                    <select
                      value={grade}
                      aria-label={isAUuser ? 'Year level' : 'Grade'}
                      onChange={(e) => setGrade(e.target.value)}
                    >
                      <option value="">{isAUuser ? 'All year levels' : 'All grades'}</option>
                      {gradeOptions.map((option) => (
                        <option key={option.label} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                  <div className={styles.sendButton}>
                    <Button type="submit" label="Send" color="green" size="small" disabled={!draft.trim() || loading} />
                  </div>
                </div>
                <p className={styles.privacy}>Please describe the situation without using students' names.</p>
              </form>
            </section>

            <aside className={styles.recommendations} aria-label="Recommended lessons">
              <h2>Recommended lessons</h2>
              {recommendations && recommendations.lessons.length > 0 ? (
                <AiLessonGrid
                  cards={recommendations.lessons}
                  isAUuser={isAUuser}
                  programName="AI Dashboard"
                  compact
                />
              ) : (
                <p className={styles.emptyRecommendations}>
                  {recommendations
                    ? 'No lessons in your library match this one yet.'
                    : 'Lessons that fit the situation will appear here.'}
                </p>
              )}
            </aside>
          </div>
        </div>
        <FooterPage />
      </div>
    </>
  );
}

export default AiDashboard;
